import { useCallback, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import Icon from 'components/generic/Icon.component';
import useOnClickOutside from 'hooks/useOnClickOutsideRef.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import {
  calendarDayKey,
  dayObjectToDate,
  DAYS_IN_WEEK,
  getDayNames,
  getDaysInMonth,
  getFirstDayOfMonth,
  getMonthNames,
  getWeekStartByLocale,
  isCalendarDayInInclusiveRange,
  isCalendarDayStrictlyInsideRange,
  isSameDay,
} from 'utils/date/customDatePicker.util';
import createNodeKey from 'utils/react/createNodeKey.util';

import CN from './CustomDatePicker.module.scss';

/**
 * CustomDatePicker - A lightweight date picker component without moment.js
 * Supports leap years automatically and can handle date range selections that span across months
 *
 * @param {Object} props
 * @param {Date} props.selectedDate - Currently selected date (controlled) - for single date mode
 * @param {Function} props.onChange - Callback when date is selected (date: Date) => void - for single date mode
 * @param {Function} props.onClose - Callback when picker should close
 * @param {Function} props.onOutsideClick - Callback when clicking outside the picker
 * @param {Date} props.minDate - Minimum selectable date (optional) - e.g. format: new Date(2024, 0, 1) for Jan 1, 2024
 * @param {Date} props.maxDate - Maximum selectable date (optional) - e.g. format: new Date(2024, 11, 31) for Dec 31, 2024
 * @param {boolean} props.inline - Whether to show inline or as dropdown (default: true)
 * @param {number} props.weekStartDay - Day to start the week (0 = Sunday, 1 = Monday) (optional, auto-detected from locale if not provided)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.selectRange - Enable date range selection mode (optional)
 * @param {Date} props.rangeStart - Start date of selected range (optional, for range selection mode)
 * @param {Date} props.rangeEnd - End date of selected range (optional, for range selection mode)
 * @param {Function} props.onRangeChange - Callback when range is selected: (rangeStart: Date, rangeEnd: Date) => void
 * @param {Date} props.highlightRangeStart - Optional inclusive range for visuals only (e.g. scheduler week).
 *     Single-click onChange still applies.
 * @param {Date} props.highlightRangeEnd - Inclusive range end (same calendar-day convention as highlightRangeStart).
 */
const CustomDatePicker = ({
  selectedDate,
  onChange,
  onClose,
  onOutsideClick,
  minDate,
  maxDate,
  inline = true,
  weekStartDay,
  className,
  selectRange = false,
  rangeStart,
  rangeEnd,
  onRangeChange,
  highlightRangeStart,
  highlightRangeEnd,
}) => {
  const { locale } = useGilt();
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const pickerRef = useRef(null);

  // Determine week start day: use prop if provided, otherwise auto-detect from locale
  const effectiveWeekStartDay = useMemo(() => (weekStartDay !== undefined ? weekStartDay : getWeekStartByLocale(locale)), [weekStartDay, locale]);

  // View date controls which month/year is displayed in the calendar
  // Initialize to selectedDate or fallback to current date
  const [viewDate, setViewDate] = useState(() => (selectedDate instanceof Date && !isNaN(selectedDate) ? new Date(selectedDate) : new Date()));

  const monthNames = useMemo(() => getMonthNames(locale), [locale]);
  const dayNames = useMemo(() => getDayNames(locale, effectiveWeekStartDay), [locale, effectiveWeekStartDay]);

  const hasHighlightRange = Boolean(highlightRangeStart && highlightRangeEnd);
  const highlightLoHi = useMemo(() => {
    if (!hasHighlightRange) return { lo: NaN, hi: NaN };
    const a = calendarDayKey(highlightRangeStart);
    const b = calendarDayKey(highlightRangeEnd);
    return { lo: Math.min(a, b), hi: Math.max(a, b) };
  }, [hasHighlightRange, highlightRangeStart, highlightRangeEnd]);

  // Close picker on outside click
  useOnClickOutside(pickerRef, () => {
    onOutsideClick?.();
    onClose?.();
  });

  const handlePrevMonth = useCallback(() => {
    setViewDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setViewDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  }, []);

  const handleDateClick = useCallback(
    (day, isFromPrevMonth = false, isFromNextMonth = false) => {
      if (!day) return;

      const dayObj = {
        day,
        isPrevMonth: isFromPrevMonth,
        isNextMonth: isFromNextMonth,
      };

      const date = dayObjectToDate(dayObj, viewDate);

      // Check if date is within min/max bounds
      if (minDate && date < minDate) return;
      if (maxDate && date > maxDate) return;

      if (selectRange) {
        // Range selection logic
        if (!rangeStart || (rangeStart && rangeEnd)) {
          // Start a new range
          onRangeChange?.(date, null);
        } else {
          // Complete the range
          if (date < rangeStart) {
            // User clicked before start, swap them
            onRangeChange?.(date, rangeStart);
          } else {
            // Normal case: end date after start date
            onRangeChange?.(rangeStart, date);
          }
          onClose?.();
        }
      } else {
        // Single date selection
        onChange(date);
        onClose?.();
      }
    },
    [viewDate, onChange, onClose, minDate, maxDate, selectRange, rangeStart, rangeEnd, onRangeChange],
  );

  // Generate calendar days including previous/next month days to complete weeks
  const calendarDays = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);

    // Adjust first day based on week start day
    const adjustedFirstDay = (firstDay - effectiveWeekStartDay + DAYS_IN_WEEK) % DAYS_IN_WEEK;

    const days = [];

    // Add days from previous month to complete the first week
    if (adjustedFirstDay > 0) {
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);

      for (let i = adjustedFirstDay - 1; i >= 0; i--) {
        days.push({
          day: daysInPrevMonth - i,
          isCurrentMonth: false,
          isPrevMonth: true,
          isNextMonth: false,
        });
      }
    }

    // Add all days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        isCurrentMonth: true,
        isPrevMonth: false,
        isNextMonth: false,
      });
    }

    // Add days from next month to complete the last week
    const remainingCells = DAYS_IN_WEEK - (days.length % DAYS_IN_WEEK);
    if (remainingCells < DAYS_IN_WEEK) {
      for (let day = 1; day <= remainingCells; day++) {
        days.push({
          day,
          isCurrentMonth: false,
          isPrevMonth: false,
          isNextMonth: true,
        });
      }
    }

    return days;
  }, [viewDate, effectiveWeekStartDay]);

  const isDateDisabled = useCallback(
    (dayObj) => {
      if (!dayObj || !dayObj.day) return false;

      const date = dayObjectToDate(dayObj, viewDate);
      if (minDate && date < minDate) return true;
      if (maxDate && date > maxDate) return true;
      return false;
    },
    [viewDate, minDate, maxDate],
  );

  const isDateSelected = useCallback(
    (dayObj) => {
      if (!dayObj || !dayObj.day) return false;

      const date = dayObjectToDate(dayObj, viewDate);

      if (selectRange && rangeStart) {
        // Check if date is within selected range
        if (rangeEnd) {
          return date >= rangeStart && date <= rangeEnd;
        }
        // Only start date selected
        return isSameDay(date, rangeStart);
      }

      if (hasHighlightRange) {
        return isCalendarDayInInclusiveRange(date, highlightRangeStart, highlightRangeEnd);
      }

      if (selectedDate) {
        return isSameDay(date, selectedDate);
      }

      return false;
    },
    [viewDate, selectedDate, selectRange, rangeStart, rangeEnd, hasHighlightRange, highlightRangeStart, highlightRangeEnd],
  );

  const isDateInRange = useCallback(
    (dayObj) => {
      if (!dayObj || !dayObj.day) return false;
      const date = dayObjectToDate(dayObj, viewDate);

      if (selectRange && rangeStart && rangeEnd) {
        return date > rangeStart && date < rangeEnd;
      }

      if (hasHighlightRange) {
        return isCalendarDayStrictlyInsideRange(date, highlightRangeStart, highlightRangeEnd);
      }

      return false;
    },
    [selectRange, rangeStart, rangeEnd, viewDate, hasHighlightRange, highlightRangeStart, highlightRangeEnd],
  );

  const isRangeStart = useCallback(
    (dayObj) => {
      if (!dayObj || !dayObj.day) return false;
      const date = dayObjectToDate(dayObj, viewDate);

      if (selectRange && rangeStart) {
        return isSameDay(date, rangeStart);
      }

      if (hasHighlightRange && !Number.isNaN(highlightLoHi.lo)) {
        return calendarDayKey(date) === highlightLoHi.lo;
      }

      return false;
    },
    [selectRange, rangeStart, viewDate, hasHighlightRange, highlightLoHi.lo],
  );

  const isRangeEnd = useCallback(
    (dayObj) => {
      if (!dayObj || !dayObj.day) return false;
      const date = dayObjectToDate(dayObj, viewDate);

      if (selectRange && rangeEnd) {
        return isSameDay(date, rangeEnd);
      }

      if (hasHighlightRange && !Number.isNaN(highlightLoHi.hi)) {
        return calendarDayKey(date) === highlightLoHi.hi;
      }

      return false;
    },
    [selectRange, rangeEnd, viewDate, hasHighlightRange, highlightLoHi.hi],
  );

  const isToday = useCallback(
    (dayObj) => {
      if (!dayObj || !dayObj.day) return false;

      const date = dayObjectToDate(dayObj, viewDate);
      const today = new Date();
      return isSameDay(date, today);
    },
    [viewDate],
  );

  return (
    <div
      ref={pickerRef}
      className={cn(CN.datePicker, className, {
        [CN.inline]: inline,
        [CN.dropdown]: !inline,
        [CN.dark]: isDarkMode,
      })}
    >
      {/* Header with month/year and navigation */}
      <div className={CN.header}>
        <button type="button" className={CN.navButton} onClick={handlePrevMonth} aria-label={t('common.datePicker.previousMonth')}>
          <Icon variant="arrowPrev" width="lg" />
        </button>

        <div className={CN.monthYear}>
          {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
        </div>

        <button type="button" className={CN.navButton} onClick={handleNextMonth} aria-label={t('common.datePicker.nextMonth')}>
          <Icon variant="arrowNext" width="lg" />
        </button>
      </div>

      {/* Day names header */}
      <div className={CN.dayNames}>
        {dayNames.map((dayName, index) => (
          <div key={index} className={CN.dayName}>
            {dayName}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className={CN.calendar}>
        {calendarDays.map((dayObj, index) => (
          <div key={createNodeKey({ index, tag: dayObj?.day })} className={CN.dayCell}>
            {dayObj && (
              <button
                type="button"
                className={cn(CN.day, {
                  [CN.selected]: isDateSelected(dayObj),
                  [CN.today]: isToday(dayObj),
                  [CN.disabled]: isDateDisabled(dayObj),
                  [CN.otherMonth]: !dayObj.isCurrentMonth,
                  [CN.prevMonth]: dayObj.isPrevMonth,
                  [CN.nextMonth]: dayObj.isNextMonth,
                  [CN.inRange]: isDateInRange(dayObj),
                  [CN.rangeStart]: isRangeStart(dayObj),
                  [CN.rangeEnd]: isRangeEnd(dayObj),
                })}
                onClick={() => handleDateClick(dayObj.day, dayObj.isPrevMonth, dayObj.isNextMonth)}
                disabled={isDateDisabled(dayObj)}
              >
                {dayObj.day}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomDatePicker;
